import {
  createAppointmentEventAsync,
  updateAppointmentEventAsync,
  rescheduleAppointmentEventAsync,
  createCalendarWithRelationsAsync,
  updateCalendarWithRelationsAsync,
  deleteCalendarAsync,
  CalendarModel,
  CoreParticipantModel,
  mapApiLocationToFrontend,
  parsePreferenceOptionRows,
  CALENDAR_THEME_OPTION,
} from "@blazeo.com/appointment-client";
import {
  EventModel as AppointmentEventModel,
  ExternalEventModel,
  CustomFieldModel,
  CalendarParticipantModel,
  ActivityLogModel,
  CalendarModel as CoreCalendarModel,
  CalendarLocationModel,
  PreferenceModel,
} from "@blazeo.com/calendar-client";
import store from "@/store";
import moment from "moment";
import { DataClientError } from "../errors";
import { Enums, FeatureKeys } from "@/services/dropdown-enums";
import { isAppointmentUpcoming, serialize } from "@/services/generic.methods";
import { getOffsetMinutes } from "@/components/public/BookAnAppointment/utils/timezone-helpers";
import storage from "@/services/storage";
import FeatureService from "@/services/feature-service";
import { isFieldIdKey } from "@/services/custom-fields/fieldKeys";

const normalizeCrmCustomFieldLink = (field) => {
  const clonedField = { ...field };
  const fieldKey = clonedField.fieldKey ?? clonedField.FieldKey;
  if (isFieldIdKey(fieldKey)) {
    const fieldId =
      clonedField.fieldId ?? clonedField.FieldId ?? Number(fieldKey);
    clonedField.fieldId = fieldId;
    clonedField.FieldId = fieldId;
    clonedField.fieldKey = String(fieldKey);
    clonedField.FieldKey = String(fieldKey);
  }
  return clonedField;
};

const pickField = (obj, ...keys) => {
  if (obj == null || typeof obj !== "object") return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
};

const unwrapCalendarGetData = (res) => {
  if (res == null || typeof res !== "object") return null;
  let data = res.data ?? res.Data;
  if (data == null) return null;
  if (typeof data === "object" && (data.data != null || data.Data != null)) {
    data = data.data ?? data.Data;
  }
  return data && typeof data === "object" ? data : null;
};

const calendarHasTheme = (calendar) => {
  const theme = pickField(calendar, "theme", "Theme") || {};
  return Boolean(
    pickField(calendar, "logoUrl", "LogoUrl") ||
    pickField(calendar, "color", "Color") ||
    pickField(theme, "logoUrl", "LogoUrl") ||
    pickField(theme, "color", "Color")
  );
};

const mapThemeFromPreference = (rawPreference) => {
  const rows = parsePreferenceOptionRows(rawPreference);
  const themeRow = rows.find((row) => {
    const name = pickField(row, "Name", "name");
    return !name || name === CALENDAR_THEME_OPTION;
  });
  if (!themeRow) return null;
  const logoUrl = String(pickField(themeRow, "logoUrl", "LogoUrl") || "").trim() || null;
  const color = String(pickField(themeRow, "color", "Color") || "").trim() || null;
  if (!logoUrl && !color) return null;
  return { logoUrl, color };
};

const applyPublicBookingFieldFilters = (fields, { isCrm } = {}) => {
  const list = Array.isArray(fields) ? fields : [];
  const verifyIsCRM = isCrm;
  let next = list.filter((field) => {
    const hasFieldId = !!(field.fieldId || field.customFieldId || field.CustomFieldId);
    const hasLeadKey = Boolean(field.fieldKey || field.FieldKey);
    return hasFieldId || hasLeadKey;
  });

  const hasCustomPhone = !verifyIsCRM && next.some((field) => {
    const kind = field.kind ?? field.Kind;
    const fieldType = field.fieldType ?? field.FieldType;
    return kind === 2 && String(fieldType || "").toLowerCase() === "phone";
  });

  next = next.map((field) => {
    const clonedField = { ...field };
    const kind = clonedField.kind ?? clonedField.Kind;
    const fieldType = clonedField.fieldType ?? clonedField.FieldType;
    if (!verifyIsCRM && kind === 2 && String(fieldType || "").toLowerCase() === "phone") {
      clonedField.fieldKey = "Phone";
      clonedField.FieldKey = "Phone";
    }
    if (!verifyIsCRM && kind === 2 && String(fieldType || "").toLowerCase() === "text") {
      clonedField.fieldKey = "Description";
      clonedField.FieldKey = "Description";
    }
    if (kind === 1) {
      delete clonedField.fieldSubType;
      delete clonedField.FieldSubType;
    }
    return clonedField;
  });

  return next.filter((field) => {
    const kind = field.kind ?? field.Kind;
    const fieldKey = field.fieldKey ?? field.FieldKey;
    if (hasCustomPhone && kind === 2 && fieldKey === "phone") {
      return false;
    }
    return true;
  });
};

export class SmartHubAppointmentAdapter {
  constructor(options = {}) {
    this.client = null;
    this.companyKey = options.companyKey || "";
    this.membersCache = null;
    this.busySlotsCache = {};
    this.isCRM =
      typeof window !== "undefined" &&
      (() => {
        try {
          const profile = JSON.parse(storage.getItem("Profile"));
          const features = FeatureService.resolveFeatures(profile);
          return FeatureService.hasFeature(features, FeatureKeys.CRM);
        } catch {
          return false;
        }
      })();
  }

  clearCache() {
    this.membersCache = null;
    this.busySlotsCache = {};
  }

  clearBusySlotsCache(participantId) {
    if (participantId) {
      delete this.busySlotsCache[participantId];
    } else {
      this.busySlotsCache = {};
    }
  }

  async getAppointments(params = {}) {
    try {
      const {
        where,
        skip,
        take,
        order,
        companyKey,
        isUpcoming = false,
        ...directParams
      } = params;

      // Extract flat filters from the complex GQL 'where' structure
      const filters = this._extractFilters(where);
      const mergedFilters = { ...filters, ...directParams };
      if (mergedFilters.visitorPhone) {
        mergedFilters.visitorPhone = String(mergedFilters.visitorPhone).replace(
          /\D/g,
          ""
        );
      }

      const {
        calendarId,
        leadId,
        email,
        startDate_gte,
        startDate_lte,
        status,
        status_neq,
        attendeeStatus,
        attendee_status,
        timeZoneId,
        ...restFilters
      } = mergedFilters;

      const parentCompanyKey = companyKey || this.companyKey;

      // Map UI filters (organizer -> participantId, guest -> leadId, search -> title, serviceType -> calendarId) to their backend equivalents
      const resolvedLeadId = leadId || restFilters.guest;
      if (restFilters.guest) {
        delete restFilters.guest;
      }

      if (restFilters.organizer || restFilters.memberId) {
        restFilters.participantId =
          restFilters.organizer || restFilters.memberId;
        delete restFilters.organizer;
        delete restFilters.memberId;
      }

      if (restFilters.search) {
        restFilters.title = restFilters.title || restFilters.search;
        delete restFilters.search;
      }

      const resolvedCalendarId = calendarId || restFilters.serviceType;
      if (restFilters.serviceType) {
        delete restFilters.serviceType;
      }

      // Resolve offset from UI-provided timezone string, fallback to current browser offset
      // POSITIONAL ARGS: Native method expects (startDateFrom, startDateTo, opts)
      // We pass the date range as the first two arguments
      // Convert to YYYY-MM-DD format as expected by the native backend
      const startDateFrom = startDate_gte
        ? moment(startDate_gte).format("YYYY-MM-DD")
        : moment().subtract(2, "years").format("YYYY-MM-DD");
      const startDateTo = startDate_lte
        ? moment(startDate_lte).format("YYYY-MM-DD")
        : moment().add(2, "years").format("YYYY-MM-DD");

      // DST-aware offset for the range start (falls back to the browser offset)
      const offset = this._resolveOffset(timeZoneId, startDateFrom);

      const resolvedStatus =
        status !== undefined
          ? status
          : attendeeStatus !== undefined
            ? attendeeStatus
            : attendee_status !== undefined
              ? attendee_status
              : status_neq !== undefined
                ? `!${status_neq}`
                : undefined;
      // OPTION MAPPING: Map adapter params to the native 'opts' schema
      const rawOpts = {
        companyKey: parentCompanyKey,
        calendarId: resolvedCalendarId,
        visitorEmail: email,
        leadId: resolvedLeadId,
        status: resolvedStatus,
        skip: skip,
        take: take,
        offset: offset,
        ...restFilters,
      };

      // Filter out unavailable (null, undefined, or empty string) values
      const opts = Object.fromEntries(
        Object.entries(rawOpts).filter(([_, v]) => v != null && v !== "")
      );

      // Handle Sorting
      const sortInfo = Array.isArray(order) ? order[0] : order;
      if (sortInfo && sortInfo.startDate) {
        opts.sort = "start_date";
        opts.sort_dir = sortInfo.startDate; // 'ASC' or 'DESC'
      }

      delete opts.query;
      opts.page = (opts.page ?? 0) + 1;

      // Date-range API is day-granular; upcoming lists need extra rows so
      // already-elapsed times today do not consume the requested page size.
      if (isUpcoming) {
        opts.take = Math.max(Number(take) || 10, 250);
      }

      const { events, totalCount } =
        await AppointmentEventModel.getByDateRangeWithFilters(
          opts.companyKey,
          startDateFrom,
          startDateTo,
          opts
        );
      let items = (events || []).map((event) =>
        this._mapAppointmentItem(event)
      );

      // Fetch busy slots (external events) if not filtering for list/grid specific fields
      const isGridFilter =
        !!(
          opts.leadId ||
          opts.visitorEmail ||
          opts.status ||
          opts.title ||
          opts.search ||
          opts.visitorPhone ||
          opts.visitorName
        ) ||
        !!params.omitBusySlots ||
        isUpcoming;
      let busySlots = [];
      if (!isGridFilter) {
        busySlots = await this._getBusySlots(opts, startDateFrom, startDateTo);
        // Omit busy slots if there is an active appointment booked on that slot in our system (same participant & overlapping time)
        busySlots = busySlots.filter((busySlot) => {
          const hasOverlappingAppt = items.some((appt) => {
            if (appt.memberId !== busySlot.memberId) return false;
            if (appt.status === 4) return false; // Exclude canceled appointments

            const apptStart = moment(appt.startDate);
            const apptEnd = moment(appt.endDate);
            const busyStart = moment(busySlot.startDate);
            const busyEnd = moment(busySlot.endDate);

            // Check if they overlap: (apptStart < busyEnd) && (apptEnd > busyStart)
            return apptStart.isBefore(busyEnd) && apptEnd.isAfter(busyStart);
          });
          return !hasOverlappingAppt;
        });
      }

      let mergedItems = [...items, ...busySlots];

      if (isUpcoming) {
        mergedItems = mergedItems.filter((item) =>
          isAppointmentUpcoming(item.startDate)
        );
        if (typeof take === "number") {
          mergedItems = mergedItems.slice(0, take);
        }
      }

      return {
        appointments: {
          totalCount: isUpcoming
            ? mergedItems.length
            : (totalCount || items.length || 0) + busySlots.length,
          items: serialize(mergedItems),
        },
      };
    } catch (error) {
      console.error("[SmartHubAdapter] Native fetch error:", error);
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getAppointmentByEventId(eventId, options = {}) {
    try {
      const offset = this._resolveOffset(
        options.timeZoneId || options.timeZone,
        options.date || options.startDate,
        options.offset
      );
      const res = await AppointmentEventModel.get(eventId, { offset });
      if (!res) throw new Error("Event not found");
      return {
        event: this._mapAppointmentItem(res),
      };
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  /** GET /event/flow/get – flowId and flowPath (survey answers) for an event. */
  async getEventFlow(eventId) {
    try {
      if (!eventId) return null;
      return await AppointmentEventModel.getFlow(String(eventId));
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  /**
   * Maps native Blazeo event properties to the GQL-style keys expected by the UI
   */
  _resolveLocationInfo(event) {
    let text = "";
    let icon = "location";
    let link = null;

    const customLocation = event.customLocation || event.CustomLocation;
    const calendarLocationValue =
      event.calendarLocationValue || event.CalendarLocationValue;
    const calendarLocation = event.calendarLocation || event.CalendarLocation;
    const customMeetingLocation =
      event.customMeetingLocation || event.CustomMeetingLocation;

    if (customLocation && typeof customLocation === "string") {
      text = customLocation;
      icon = "location";
    } else if (calendarLocationValue != null) {
      const val = String(calendarLocationValue).trim();
      text = val;
      if (/^(https?:\/\/|www\.)[^\s]+/i.test(val)) {
        icon = "link";
      } else if (/^\+?[\d\s\-()]+$/.test(val) && /[0-9]/.test(val)) {
        icon = "phone";
      } else {
        icon = "location";
      }
    } else if (calendarLocation && typeof calendarLocation === "object") {
      const type =
        calendarLocation.type !== undefined
          ? calendarLocation.type
          : calendarLocation.locationType;
      const val = calendarLocation.value || calendarLocation.name || "";
      text = val;

      switch (type) {
        case 2: // Phone
          icon = "phone";
          break;
        case 0: // Physical
          icon = "location";
          break;
        case 1: // Video
          icon = "link";
          break;
        case 3: // Custom
          icon = "custom";
          text = val || "Ask Invitee";
          break;
      }
    } else {
      text = customMeetingLocation || "N/A";
      icon = "video";
    }

    const normalizedRawText =
      typeof text === "string" && text.startsWith("www.")
        ? "https://" + text
        : text;
    const isUrl =
      typeof normalizedRawText === "string" &&
      (normalizedRawText.startsWith("http://") ||
        normalizedRawText.startsWith("https://"));

    if (isUrl) {
      link = normalizedRawText;
      const lower = normalizedRawText.toLowerCase();
      if (lower.includes("google.com") || lower.includes("meet.google")) {
        text = "Google Meet";
      } else if (lower.includes("zoom.us")) {
        text = "Zoom";
      } else if (
        lower.includes("teams.microsoft") ||
        lower.includes("teams.live")
      ) {
        text = "Microsoft Teams";
      } else if (lower.includes("skype.com")) {
        text = "Skype";
      } else if (lower.includes("webex.com")) {
        text = "Webex";
      }
    }

    return {
      text: text || "Default Location",
      icon,
      link,
    };
  }

  _mapAppointmentItem(event) {
    if (!event) return null;
    const locationInfo = this._resolveLocationInfo(event);
    let resolvedStatus = event.status;
    // if (resolvedStatus === undefined || resolvedStatus === null) {
    //   const attStatus = event.attendeeStatus !== undefined ? event.attendeeStatus : event.AttendeeStatus;
    //   if (attStatus === 0) {
    //     resolvedStatus = 3; // Completed
    //   } else if (attStatus === 1) {
    //     resolvedStatus = 1; // Scheduled
    //   } else if (attStatus === 2) {
    //     resolvedStatus = 4; // Canceled (Rejected -> Canceled)
    //   } else if (attStatus === 3) {
    //     resolvedStatus = 1; // Scheduled
    //   } else if (attStatus === 5) {
    //     resolvedStatus = 4; // Canceled
    //   } else {
    //     resolvedStatus = attStatus;
    //   }
    // }

    return {
      ...event,
      id: event.eventId || event.EventId || event.id || event.Id,
      status: resolvedStatus,
      memberId: event.participantId || event.ParticipantId,
      calendarId: event.calendarId || event.CalendarId,
      startDate: event.startDate || event.StartDate,
      endDate: event.endDate || event.EndDate,
      timeZone: event.timeZone || event.TimeZone,
      // Convert to Date objects for UI/Calendar reliability
      start: event.startDate
        ? moment(event.startDate).toDate()
        : event.StartDate
          ? moment(event.StartDate).toDate()
          : null,
      end: event.endDate
        ? moment(event.endDate).toDate()
        : event.EndDate
          ? moment(event.EndDate).toDate()
          : null,
      // Ensure visitor fields are consistent
      email: event.visitorEmail || event.VisitorEmail,
      name: event.visitorName || event.VisitorName,
      phone: event.visitorPhone || event.VisitorPhone,
      leadSource:
        event.leadSource ||
        event.LeadSource ||
        event.source ||
        event.Source ||
        "",
      // Resolved Location Information
      locationText: locationInfo.text,
      locationIcon: locationInfo.icon,
      locationLink: locationInfo.link,
    };
  }

  async _getBusySlots(opts, startDateFrom, startDateTo) {
    let participantIds = [];
    if (opts.participantId) {
      participantIds = [opts.participantId];
    } else {
      try {
        const state = store.getState();
        const membersList = state?.appointment?.members || [];
        if (membersList.length > 0) {
          participantIds = membersList
            .map((m) => m.memberId || m.participantId || m.id)
            .filter(Boolean);
        } else {
          const membersData = await this.getMembers({
            companyKey: opts.companyKey,
          });
          const fetchedMembersList = membersData?.members?.items || [];
          participantIds = fetchedMembersList
            .map((m) => m.memberId || m.participantId || m.id)
            .filter(Boolean);
        }
      } catch (err) {
        console.error(
          "Failed to get members from store/cache for busy slots:",
          err
        );
      }
    }

    if (participantIds.length === 0) {
      return [];
    }

    try {
      const fromMoment = moment(startDateFrom).startOf("day");
      const toMoment = moment(startDateTo).endOf("day");
      const results = await Promise.all(
        participantIds.map(async (pId) => {
          try {
            let slots;
            if (this.busySlotsCache[pId]) {
              slots = this.busySlotsCache[pId];
            } else {
              slots =
                (await ExternalEventModel.getByParticipant(pId, {
                  offset: opts.offset,
                })) || [];
              this.busySlotsCache[pId] = slots;
            }

            return (slots || [])
              .filter((slot) => {
                const sDate = slot.startDate || slot.StartDate;
                if (!sDate) return false;
                const startM = moment(sDate);
                return (
                  startM.isSameOrAfter(fromMoment) &&
                  startM.isSameOrBefore(toMoment)
                );
              })
              .map((slot) => ({
                id: slot.id || `busy-${crypto.randomUUID()}`,
                title: "Busy",
                startDate: slot.startDate || slot.StartDate,
                endDate: slot.endDate || slot.EndDate,
                start:
                  slot.startDate || slot.StartDate
                    ? moment(slot.startDate || slot.StartDate).toDate()
                    : null,
                end:
                  slot.endDate || slot.EndDate
                    ? moment(slot.endDate || slot.EndDate).toDate()
                    : null,
                participantId: pId,
                memberId: pId,
                calendarId: slot.calendarId || slot.CalendarId,
                status: slot.status || slot.Status || 1,
                isBusy: true,
                color: { code: "#E4E4E6", textCode: "#6E6E73" },
              }));
          } catch (e) {
            console.error(
              `Failed to fetch busy slots for participant ${pId}:`,
              e
            );
            return [];
          }
        })
      );

      return results.flat();
    } catch (e) {
      console.error("Failed to fetch busy slots:", e);
      return [];
    }
  }

  /**
   * Internal helper to resolve lead email from ID using LeadModel
   */
  async _getLeadEmailById(leadId) {
    try {
      const { LeadModel } = await import("@blazeo.com/appointment-client");
      const lead = await LeadModel.get(String(leadId));
      return lead?.email || null;
    } catch (err) {
      console.warn(`Could not resolve email for leadId ${leadId}:`, err);
      return null;
    }
  }

  /**
   * Recursively parses the GraphQL-style 'where' object into a flat filters object
   */
  _extractFilters(where) {
    const filters = {};
    if (!where) return filters;

    const processItem = (item) => {
      if (!item) return;
      Object.keys(item).forEach((key) => {
        const val = item[key];
        if (key === "and" || key === "or") {
          if (Array.isArray(val)) val.forEach(processItem);
        } else if (typeof val === "object" && val !== null) {
          // Map GQL operators to internal keys
          if (val.gte !== undefined) filters[`${key}_gte`] = val.gte;
          if (val.gt !== undefined) filters[`${key}_gte`] = val.gt;
          if (val.lte !== undefined) filters[`${key}_lte`] = val.lte;
          if (val.lt !== undefined) filters[`${key}_lte`] = val.lt;
          if (val.eq !== undefined) filters[key] = val.eq;
          if (val.neq !== undefined) filters[`${key}_neq`] = val.neq;
          if (val.in !== undefined) {
            filters[key] = Array.isArray(val.in) ? val.in.join(",") : val.in;
          }
          if (val.contains !== undefined) {
            filters[`${key}_contains`] = val.contains;
            filters[key] = val.contains;
          }
        } else {
          filters[key] = val;
        }
      });
    };

    processItem(where);
    return filters;
  }

  async getCalendars(params = {}) {
    try {
      const companyKey = params.companyKey || this.companyKey;
      if (!companyKey) {
        throw new Error(
          "companyKey is required to fetch calendars in SmartHub adapter"
        );
      }

      const result = await CalendarModel.getCalendarsByCompany(companyKey, {
        sortBy: "createdOn",
        sortOrder: "desc",
      });
      // Native result is often { Calendars: [...] } or just [...]
      const rawItems = result?.calendars || result || [];
      const mappedItems = await Promise.all(
        (Array.isArray(rawItems) ? rawItems : []).map(async (c) => {
          const mappedMembers = await Promise.all(
            (c.members || []).map(async (m) => {
              const name =
                m.name ||
                m.Name ||
                m.alias ||
                m.Alias ||
                m.userSsoEmail ||
                m.UserSsoEmail ||
                m.email ||
                m.Email;
              if (name) {
                return {
                  ...m,
                  name,
                };
              }
              const memberId = m.id || m.Id || m.participantId || m.memberId;
              if (memberId) {
                try {
                  const part = await CoreParticipantModel.get(memberId);
                  if (part) {
                    const resolvedName =
                      part.name ||
                      part.Name ||
                      part.alias ||
                      part.Alias ||
                      part.userSsoEmail ||
                      part.UserSsoEmail ||
                      part.email ||
                      part.Email ||
                      "Member";
                    return {
                      ...m,
                      ...part,
                      name: resolvedName,
                    };
                  }
                } catch (err) {
                  console.error(
                    `Failed to fetch core participant ${memberId}:`,
                    err
                  );
                }
              }
              return {
                ...m,
                name: "Member",
              };
            })
          );

          return {
            ...c,
            id: c.calendarId || c.CalendarId || c.Id || c.id,
            calendarId: c.calendarId || c.CalendarId || c.Id || c.id,
            name: c.Title || c.Name || c.name || "Untitled Calendar",
            members: mappedMembers,
          };
        })
      );

      return {
        calendars: {
          totalCount: result?.totalCount || mappedItems.length,
          items: serialize(mappedItems),
        },
      };
    } catch (error) {
      console.error("[SmartHubAdapter] Fetch calendars error:", error);
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getCalendarById(calendar) {
    try {
      const calendarId = calendar?.uuid || calendar?.calendarId || calendar.id;
      const result = await CalendarModel.fetchCalendarDetails(calendarId, {
        isCrm: this.isCRM,
      });
      if (result.appointmentUserDefinedFields) {
        const verifyIsCRM = result?.crmLeadCustomFields?.length || this.isCRM;
        result.appointmentUserDefinedFields =
          result.appointmentUserDefinedFields.filter((field) => {
            const kind = field.kind ?? field.Kind;
            const key = String(
              (field.fieldKey ?? field.FieldKey) || ""
            ).toLowerCase();
            const isMandatoryField = [
              "firstname",
              "lastname",
              "email",
            ].includes(key);
            if (verifyIsCRM && kind === 2 && !isMandatoryField) {
              return false;
            }
            const hasFieldId = !!field.fieldId;
            const hasMandatoryFields = !field.fieldId && isMandatoryField;
            return hasFieldId || hasMandatoryFields;
          });
        const hasCustomPhone =
          !verifyIsCRM &&
          result.appointmentUserDefinedFields.some((field) => {
            const kind = field.kind ?? field.Kind;
            const fieldType = field.fieldType ?? field.FieldType;
            return (
              kind === 2 && String(fieldType || "").toLowerCase() === "phone"
            );
          });

        // Map fieldKey to 'Phone' / 'Description' for custom fields in appointment-only mode
        result.appointmentUserDefinedFields =
          result.appointmentUserDefinedFields.map((field) => {
            const clonedField = normalizeCrmCustomFieldLink({ ...field });
            const kind = clonedField.kind ?? clonedField.Kind;
            const fieldType = clonedField.fieldType ?? clonedField.FieldType;
            if (
              !verifyIsCRM &&
              kind === 2 &&
              String(fieldType || "").toLowerCase() === "phone"
            ) {
              clonedField.fieldKey = "Phone";
              clonedField.FieldKey = "Phone";
            }
            if (
              !verifyIsCRM &&
              kind === 2 &&
              String(fieldType || "").toLowerCase() === "text"
            ) {
              clonedField.fieldKey = "Description";
              clonedField.FieldKey = "Description";
            }
            if (kind === 1) {
              delete clonedField.fieldSubType;
              delete clonedField.FieldSubType;
            }
            return clonedField;
          });
        // Filter out duplicate standard phone fields if a custom one is present
        result.appointmentUserDefinedFields =
          result.appointmentUserDefinedFields.filter((field) => {
            const kind = field.kind ?? field.Kind;
            const fieldKey = field.fieldKey ?? field.FieldKey;
            if (hasCustomPhone && kind === 2 && fieldKey === "phone") {
              return false;
            }
            return true;
          });
      }
      if (!result.bookingPageTitle) {
        result.bookingPageTitle = "Personal Information"; // just placeholder as we don't have a way to get the booking page title
      }
      return {
        calendar: serialize(result),
      };
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getPublicBookingCalendar(calendarId) {
    try {
      const id = calendarId?.uuid || calendarId?.calendarId || calendarId?.id || calendarId;
      const rawRes = await CoreCalendarModel.getRaw(id);
      const payload = unwrapCalendarGetData(rawRes);
      if (!payload) {
        throw new Error("Calendar not found");
      }

      const uuid = pickField(payload, "uuid", "Uuid", "calendarId", "CalendarId") || id;
      const rawTheme = pickField(payload, "theme", "Theme");
      let theme = rawTheme
        ? {
            id: pickField(rawTheme, "id", "Id"),
            color: pickField(rawTheme, "color", "Color") || null,
            logoUrl: pickField(rawTheme, "logoUrl", "LogoUrl") || null,
          }
        : null;

      const [themePreference, locationRows] = await Promise.all([
        calendarHasTheme(payload)
          ? Promise.resolve(null)
          : PreferenceModel.get(CALENDAR_THEME_OPTION, uuid).catch((error) => {
              console.error("[SmartHubAdapter] Fetch public calendar theme error:", error);
              return null;
            }),
        CalendarLocationModel.getByCalendar(uuid).catch((error) => {
          console.error("[SmartHubAdapter] Fetch public calendar locations error:", error);
          return [];
        }),
      ]);

      if (!calendarHasTheme({ ...payload, theme }) && themePreference) {
        const mappedTheme = mapThemeFromPreference(themePreference);
        if (mappedTheme) {
          theme = {
            ...(theme && typeof theme === "object" ? theme : {}),
            ...mappedTheme,
          };
        }
      }

      const appointmentLocations = (Array.isArray(locationRows) ? locationRows : [])
        .map((row) => {
          const mapped = mapApiLocationToFrontend(row);
          if (!mapped) return null;
          const locationId =
            mapped.calendarLocationId ||
            pickField(row, "calendarLocationId", "CalendarLocationId");
          return {
            ...mapped,
            id: locationId,
            isActive: pickField(row, "isActive", "IsActive") !== false,
          };
        })
        .filter(Boolean);

      const calendar = {
        ...payload,
        id: pickField(payload, "id", "Id") ?? payload.id,
        calendarId: uuid,
        uuid,
        name: pickField(payload, "name", "Name"),
        description: pickField(payload, "description", "Description") ?? "",
        duration: pickField(payload, "duration", "Duration"),
        durationUnit: pickField(payload, "durationUnit", "DurationUnit"),
        futureLimit: pickField(payload, "futureLimit", "FutureLimit"),
        futureLimitUnit: pickField(payload, "futureLimitUnit", "FutureLimitUnit"),
        timeZoneId: pickField(payload, "timeZoneId", "TimeZoneId"),
        companyKey: pickField(payload, "companyKey", "CompanyKey", "company_key"),
        bookingPageTitle:
          pickField(payload, "bookingPageTitle", "BookingPageTitle") || "Personal Information",
        logoUrl: pickField(payload, "logoUrl", "LogoUrl") || theme?.logoUrl || null,
        color: pickField(payload, "color", "Color") || theme?.color || null,
        themeColor: pickField(payload, "themeColor", "ThemeColor") || theme?.color || null,
        theme,
        appointmentLocations,
      };
      delete calendar.appointmentUserDefinedFields;
      delete calendar.AppointmentUserDefinedFields;
      delete calendar.crmLeadCustomFields;

      return {
        calendar: serialize(calendar),
      };
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getPublicBookingFields(calendarId, options = {}) {
    try {
      const id = calendarId?.uuid || calendarId?.calendarId || calendarId?.id || calendarId;
      const companyKey = options.companyKey || "";
      // Public booking has no Profile. This is an appointment-client fetch option
      // (CRM lead-fields API), not a calendar GET field. getCalendarById still uses this.isCRM.
      const result = await CalendarModel.getBookingFields(id, {
        format: "frontend",
        isCrm: true,
        ...(companyKey ? { companyKey } : {}),
      });
      if (!result?.ok) {
        throw new Error(result?.error || "Failed to fetch booking fields");
      }

      const combinedFields = [
        ...(result.leadFields || []).map((field) => ({
          ...field,
          kind: field.kind ?? field.Kind ?? 2,
        })),
        ...(result.customQuestions || []).map((field) => ({
          ...field,
          kind: field.kind ?? field.Kind ?? 1,
        })),
        ...(result.customFields || []),
      ];
      const hasCrmLeadFields = Array.isArray(result.crmLeadCustomFields)
        && result.crmLeadCustomFields.length > 0;
      const fields = applyPublicBookingFieldFilters(combinedFields, {
        isCrm: hasCrmLeadFields,
      });

      return serialize({
        fields,
        crmLeadCustomFields: hasCrmLeadFields ? result.crmLeadCustomFields : [],
        isCrm: Boolean(result.isCrm) || hasCrmLeadFields,
      });
    } catch (error) {
      console.error("[SmartHubAdapter] Fetch public booking fields error:", error);
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async checkAuthorizationByCalendar(calendarId) {
    try {
      const result =
        await CalendarParticipantModel.checkAuthorizationByCalendar(calendarId);
      return result;
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getPublicPayload(urlShortCode) {
    throw new Error(
      "getPublicPayload is not supported via shortCode in this version of SmartHub adapter"
    );
  }

  async getFormData(calendarId) {
    try {
      const result = await CustomFieldModel.getFormData(calendarId);
      return {
        formData: serialize(result),
      };
    } catch (error) {
      console.error("[SmartHubAdapter] Fetch form data error:", error);
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getEventCustomFields(calendarId, eventId) {
    try {
      const result = await CustomFieldModel.getFormData(calendarId, eventId);
      return serialize(result);
    } catch (error) {
      console.error(
        "[SmartHubAdapter] Fetch event custom fields error:",
        error
      );
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getBookingFormFields(calendarId) {
    try {
      const result = await CustomFieldModel.getForm(calendarId);
      return serialize(result);
    } catch (error) {
      console.error(
        "[SmartHubAdapter] Fetch booking form fields error:",
        error
      );
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  /**
   * Fetches the activity log entry for a specific appointment event (CRM mode).
   * Queries ActivityLogModel.get by eventId filtered to appointment activity types
   * (appointmentScheduled=9, appointmentReScheduled=10, appointmentCanceled=11).
   * Returns the userDefinedFields from the matched log entry, or null if not found.
   */
  async getAppointmentActivityLog(eventId, companyKey) {
    try {
      const response = await ActivityLogModel.get({
        companyKey: companyKey || this.companyKey,
        eventId: String(eventId),
        activityTypeIds: [9, 10, 11],
      });

      const logs = response?.activityLogs ?? [];
      if (!logs.length) return null;

      const log = logs[0];
      const userDefinedFields = log?.userDefinedFields ?? null;
      return userDefinedFields ? serialize(userDefinedFields) : null;
    } catch (error) {
      console.error(
        "[SmartHubAdapter] Fetch appointment activity log error:",
        error
      );
      return null;
    }
  }

  _mapAppointmentToEventSnapshot(input) {
    const start = moment(input.startDate);
    const end = moment(input.endDate);

    // Normalize Guid helper
    const normalizeGuid = (id) =>
      String(id || "")
        .trim()
        .replace(/^\{|\}$/g, "");

    return {
      calendarId: normalizeGuid(input.calendarId),
      participantId: normalizeGuid(input.participantId),
      title: input.title || null,
      description: input.description || null,
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
      startHour: start.hour(),
      startMinute: start.minute(),
      endHour: end.hour(),
      endMinute: end.minute(),
      visitorName: (input.visitorName || "").trim() || null,
      visitorEmail: input.email || input.visitorEmail || null,
      visitorPhone: input.phone || input.visitorPhone || null,
      leadId: (() => {
        const id = String(input.leadId || input.LeadId || "").trim();
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
          ? id
          : undefined;
      })(),
      calendarLocationId: input.calendarLocationId
        ? String(input.calendarLocationId).trim()
        : undefined,
      customLocation: input.customLocation
        ? String(input.customLocation).trim()
        : undefined,
      rescheduleLink: input.rescheduleUrl || null,
      cancelLink: input.cancelUrl || null,
      timeZone: input.timeZone || null,
      userDefinedFields: input.userDefinedFields || null,
      crmLeadCustomFields: input.crmLeadCustomFields || null,
      crmLeadCustomFieldsData: input.crmLeadCustomFieldsData || null,
      flowId: input.flowId || undefined,
      flowPath: input.flowPath || undefined,
    };
  }

  async createPublicAppointment(payload) {
    try {
      const offsetMinutes = this._resolveOffset(
        payload.timeZone || payload.timeZoneId,
        payload.startDate || payload.date,
        payload.offsetMinutes
      );
      const source = payload.source || Enums.BasicLeadSource.SmartHub;

      // Extract phone and description from customFieldsData in a single loop
      if (payload.customFieldsData && payload.customFieldsData.length) {
        let phoneItem = null;
        let descItem = null;

        payload.customFieldsData.forEach((item) => {
          if (!item) return;
          const kind = item.Kind || item.kind;
          const label = (item.Label || "").toLowerCase().trim();
          const name = (item.Name || "").toLowerCase().trim();
          const fieldType = (
            item.FieldType ||
            item.fieldType ||
            item.Type ||
            item.type ||
            ""
          )
            .toLowerCase()
            .trim();
          if (kind === 2) {
            if (
              label === "phone" ||
              label.includes("phone") ||
              name === "phone" ||
              fieldType === "phone"
            ) {
              phoneItem = item;
            } else if (
              label === "description" ||
              label.includes("description") ||
              name === "description" ||
              fieldType === "text"
            ) {
              descItem = item;
            }
          }
        });

        if (phoneItem && !payload.visitorPhone && !payload.phone) {
          const countryCode = payload.userDefinedFields?.CountryCode || "";
          const phoneVal = (phoneItem.Value || phoneItem.value || "").trim();
          payload.visitorPhone = phoneVal
            ? countryCode + phoneVal.replace(/\D/g, "")
            : null;
        }

        if (descItem && !payload.description) {
          payload.description = descItem.Value || descItem.value || "";
        }

        // Filter out items of Kind: 2 (LeadField) from customFieldsData, only keep Kind: 1 (Question)
        payload.customFieldsData = payload.customFieldsData.filter(
          (item) => item && (item.Kind === 1 || item.kind === 1)
        );
      }
      const snapshot = this._mapAppointmentToEventSnapshot(payload);
      const res = await AppointmentEventModel.createEvent(snapshot, {
        offset: offsetMinutes,
        source,
      });
      if (!res) throw new Error("Event creation failed");

      if (res.eventId || res.EventId) {
        // Save custom fields (user-defined fields responses) under event ID
        const id = res.eventId || res.EventId;
        if (payload.customFieldsData && payload.customFieldsData.length) {
          try {
            await CustomFieldModel.saveFormData(id, payload.customFieldsData);
          } catch (cfError) {
            console.error("Failed to save custom fields data:", cfError);
          }
        }
        try {
          await AppointmentEventModel.setEventStatus(id, 1);
        } catch (e) {
          console.error("Failed to set event status on creation:", e);
        }
      }

      return {
        ok: true,
        event: res ? this._mapAppointmentItem(res) : res,
      };
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async createAppointment(payload) {
    try {
      const offsetMinutes = this._resolveOffset(
        payload.timeZone || payload.timeZoneId,
        payload.startDate || payload.date,
        payload.offsetMinutes
      );
      const source = payload.source || Enums.BasicLeadSource.SmartHub;
      const result = await createAppointmentEventAsync(payload, {
        offsetMinutes,
        source,
      });
      if (!result.ok) throw new Error(result.error);
      if (result.event?.eventId || result.event?.EventId) {
        const id = result.event.eventId || result.event.EventId;
        try {
          await AppointmentEventModel.setEventStatus(id, 1);
        } catch (e) {
          console.error("Failed to set event status on creation:", e);
        }
      }
      return {
        ...result,
        event: result.event
          ? this._mapAppointmentItem(result.event)
          : result.event,
      };
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async updateAppointment(payload) {
    try {
      const offsetMinutes = this._resolveOffset(
        payload.timeZone || payload.timeZoneId,
        payload.startDate || payload.date,
        payload.offsetMinutes
      );
      const source = payload.source || Enums.BasicLeadSource.SmartHub;
      const result = await updateAppointmentEventAsync(payload, {
        offsetMinutes,
        source,
      });
      if (!result.ok) throw new Error(result.error);
      if (result.event?.eventId || result.event?.EventId) {
        const id = result.event.eventId || result.event.EventId;
        try {
          await AppointmentEventModel.setEventStatus(id, 5);
          if (result.event) result.event.status = 5;
        } catch (e) {
          console.error("Failed to set event status on update:", e);
        }
      }
      return {
        ...result,
        event: result.event ? this._mapAppointmentItem(result.event) : result.event,
      };
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async rescheduleAppointment(payload) {
    try {
      const offsetMinutes = this._resolveOffset(
        payload.timeZone || payload.timeZoneId,
        payload.startDate || payload.date,
        payload.offsetMinutes
      );
      const result = await rescheduleAppointmentEventAsync(payload, {
        offsetMinutes,
      });
      if (!result.ok) throw new Error(result.error);
      if (result.event?.eventId || result.event?.EventId) {
        const id = result.event.eventId || result.event.EventId;
        try {
          await AppointmentEventModel.setEventStatus(id, 2);
        } catch (e) {
          console.error("Failed to set event status on reschedule:", e);
        }
      }
      return {
        ...result,
        event: result.event
          ? this._mapAppointmentItem(result.event)
          : result.event,
      };
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async cancelAppointment(appointmentEventId, cancellationReason) {
    try {
      const result = await AppointmentEventModel.cancel(
        appointmentEventId,
        cancellationReason
      );
      if (!result.ok && result.status !== "success")
        throw new Error(result.error || result.message);
      try {
        await AppointmentEventModel.setEventStatus(appointmentEventId, 4);
      } catch (e) {
        console.error("Failed to set event status on cancellation:", e);
      }
      return result;
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async deleteAppointment(appointmentEventId) {
    try {
      const result = await AppointmentEventModel.delete(appointmentEventId);
      return result;
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async completeAppointment(appointmentEventId) {
    try {
      const res = await AppointmentEventModel.setEventStatus(
        appointmentEventId,
        3
      );
      return { ok: true, data: res };
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async saveCalendar(params) {
    try {
      const {
        appointmentHub,
        methodName,
        responseMethodName,
        deletedCustomFieldIds,
        ...payload
      } = params;
      const isUpdate = !!payload.calendarId;
      payload.calendarId = payload.calendarId || payload.uuid || payload.id;
      const saveFn = isUpdate
        ? updateCalendarWithRelationsAsync
        : createCalendarWithRelationsAsync;

      payload.openingHours = payload.openingHours.map((oh) => {
        const clonedOh = {
          ...oh,
          participantId: oh.member,
        };
        delete clonedOh.member;
        return clonedOh;
      });

      if (payload.appointmentUserDefinedFields) {
        const verifyIsCRM = payload.crmLeadCustomFields?.length || this.isCRM;
        payload.appointmentUserDefinedFields =
          payload.appointmentUserDefinedFields
            .filter((field) => {
              const kind = field.kind ?? field.Kind;
              const key = String(
                (field.fieldKey ?? field.FieldKey) || ""
              ).toLowerCase();
              const isMandatoryField = [
                "firstname",
                "lastname",
                "email",
              ].includes(key);
              if (verifyIsCRM && kind === 2 && !isMandatoryField) {
                return false;
              }
              return true;
            })
            .map((field) => {
              const clonedField = normalizeCrmCustomFieldLink({ ...field });
              const kind = clonedField.kind ?? clonedField.Kind;
              clonedField.Kind = kind;
              clonedField.kind = kind;
              if (kind === 1) {
                clonedField.IsMandatory = false;
                clonedField.isMandatory = false;
                delete clonedField.fieldSubType;
                delete clonedField.FieldSubType;
              }
              return clonedField;
            });
      }
      const result = await saveFn({
        ...payload,
        deletedCustomFieldIds,
        companyKey: payload.companyKey || this.companyKey,
        isCRM: this.isCRM,
      });
      if (!result.ok) throw new Error(result.error);
      return result;
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async deleteCalendar(params) {
    try {
      const { id } = params;
      const result = await deleteCalendarAsync(id);
      if (!result.ok) throw new Error(result.error);
      return result;
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getMembers(params = {}) {
    try {
      const companyKey = params.companyKey || this.companyKey;
      if (!companyKey) {
        throw new Error(
          "companyKey is required to fetch participants in SmartHub adapter"
        );
      }
      if (this.membersCache) {
        return {
          members: {
            totalCount: this.membersCache.length,
            items: this.membersCache,
          },
        };
      }
      const result = await CoreParticipantModel.getAll(companyKey);
      // Handle potential nesting (Participants) or direct array
      const rawItems = result?.Participants || result || [];
      const mappedItems = (Array.isArray(rawItems) ? rawItems : []).map((p) => {
        const member = serialize(p);
        const rawIntId = p.Id ?? p.id ?? member.Id ?? member.id;
        const parsedIntId = Number(rawIntId);
        const intId = Number.isFinite(parsedIntId) ? parsedIntId : null;
        if (member.status === undefined) {
          member.status = member.isApproved
            ? Enums.ConnectEmailStatus.Connected
            : Enums.ConnectEmailStatus.NotConnected;
        }

        return {
          ...member,
          intId,
          memberId: p.participantId || p.Id || p.id,
          id: p.participantId || p.Id || p.id,
          name:
            p?.Alias ||
            p?.alias ||
            p?.Name ||
            p?.name ||
            p?.DisplayName ||
            p?.email ||
            "Untitled Member",
        };
      });

      this.membersCache = mappedItems;

      return {
        members: {
          totalCount: mappedItems.length,
          items: mappedItems,
        },
      };
    } catch (error) {
      console.error("[SmartHubAdapter] Fetch members error:", error);
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async deleteMember(params) {
    try {
      if (!params || !params.id) {
        throw new Error("id is required to delete a member");
      }
      const response = await CoreParticipantModel.remove(params.id);
      if (this.membersCache) {
        this.membersCache = this.membersCache.filter(
          (m) => String(m.id ?? m.memberId) !== String(params.id)
        );
      }
      if (this.busySlotsCache[params.id]) {
        delete this.busySlotsCache[params.id];
      }
      return response;
    } catch (error) {
      console.error("[SmartHubAdapter] Delete member error:", error);
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getTimeZones() {
    try {
      const res = await CalendarModel.getTimeZones();
      const result = Array.isArray(res)
        ? res
        : (res?.timeZones ?? res?.data?.timeZones);
      return { timeZones: result ?? [] };
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getTimeZone(timezoneId) {
    try {
      const res = await CalendarModel.getTimeZone(timezoneId);
      return res;
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getAvailabilityByDate(params = {}) {
    try {
      const { calendarId, date, offsetMinutes, timeZoneId, timeZone } = params;
      if (!calendarId || !date) {
        // throw new Error("calendarId and date are required");
        return;
      }

      // Robust parsing: Try YYYY-MM-DD first to avoid timezone shifts, fallback to moment
      let year, month;
      const parts = date.split("-");
      if (parts.length >= 2 && parts[0].length === 4) {
        year = Number(parts[0]);
        month = Number(parts[1]);
      } else {
        const momentDate = moment(date);
        year = momentDate.year();
        month = momentDate.month() + 1;
      }

      const offset = this._resolveOffset(
        timeZoneId || timeZone,
        date,
        offsetMinutes
      );

      const daysSelectableMap = await AppointmentEventModel.getDaysSelectable(
        calendarId,
        year,
        month,
        {
          offset,
        }
      );

      return daysSelectableMap;
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getSlotsByDate(params = {}) {
    try {
      const { calendarId, date, offsetMinutes, timeZoneId, timeZone } = params;
      if (!calendarId || !date) {
        throw new Error("calendarId and date are required");
      }

      // Robust parsing: Try YYYY-MM-DD first to avoid timezone shifts, fallback to moment
      let year, month, day;
      const parts = date.split("-");
      if (parts.length === 3 && parts[0].length === 4) {
        [year, month, day] = parts.map(Number);
      } else {
        const m = moment(date);
        year = m.year();
        month = m.month() + 1;
        day = m.date();
      }

      const offset = this._resolveOffset(
        timeZoneId || timeZone,
        date,
        offsetMinutes
      );

      const items = await AppointmentEventModel.getAvailability(
        calendarId,
        year,
        month,
        day,
        {
          offset,
        }
      );

      const mappedItems = (items || []).map((slot) => ({
        startDate: slot.startDate,
        endDate: slot.endDate,
        participantId: slot.participantId,
        startHour: slot.startHour,
        startMinute: slot.startMinute,
        endHour: slot.endHour,
        endMinute: slot.endMinute,
      }));

      return {
        timeSlot: {
          items: mappedItems,
        },
      };
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  /**
   * Offset in minutes for an appointment instant.
   * A timezone id is evaluated at `atDate` so daylight saving is included.
   * Falls back to an explicit offset, then the browser offset.
   * @param {string} [timezoneStr]
   * @param {string|Date|import("moment").Moment} [atDate]
   * @param {number} [fallbackOffset]
   * @returns {number}
   */
  _resolveOffset(timezoneStr, atDate, fallbackOffset) {
    if (timezoneStr) {
      return this._parseOffset(timezoneStr, atDate);
    }
    if (
      fallbackOffset !== undefined &&
      fallbackOffset !== null &&
      fallbackOffset !== ""
    ) {
      const numeric = Number(fallbackOffset);
      if (!Number.isNaN(numeric)) return numeric;
    }
    return moment().utcOffset();
  }

  /**
   * Windows / IANA timezone string → UTC offset in minutes (DST-aware).
   * @param {string} timezoneStr
   * @param {string|Date|import("moment").Moment} [atDate]
   * @returns {number}
   */
  _parseOffset(timezoneStr, atDate) {
    if (!timezoneStr) return moment().utcOffset();

    try {
      const timezones = store.getState()?.general?.timezones || [];
      return getOffsetMinutes(timezoneStr, timezones, atDate);
    } catch (e) {
      console.error("Failed to resolve timezone offset:", e);
      return moment().utcOffset();
    }
  }

  async getCountsByCompany(companyKey) {
    try {
      const parentCompanyKey = companyKey || this.companyKey;
      const response =
        await AppointmentEventModel.getCountsByCompany(parentCompanyKey);
      return response;
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }

  async getLeads(params = {}) {
    try {
      const companyKey = params.companyKey || this.companyKey;
      const { filter, page = 1, page_size = 20 } = params;
      const opts = { page, page_size, sort: "id", sort_dir: "desc" };
      const items = await LeadModel.getByCompany(companyKey, opts);
      return {
        leadsGrid: {
          totalCount: items?.length || 0,
          items: serialize(items),
        },
      };
    } catch (error) {
      throw new DataClientError("blazeo", 500, error.message, error);
    }
  }
}
